// ============================================================================
// Formats PLANIFICATION SALARIALE (L05) — helpers locale-aware (FR+EN)
// ============================================================================
// Meme raison que finance/format.ts + accueil/format.ts : les helpers historiques
// src/lib/format/currency.ts sont fr-CA FIGES (heritage PoC). L'ecran L05 est
// bilingue natif (P14 PLAN-ATTAQUE) — chaque format prend la locale i18n courante
// (`useTranslation().i18n.language`). Module par ecran = pattern etabli du parc.
// Regle #4 : zero emoji. Fonctions pures -> testables.

const localeTag = (locale: string): string => (locale.startsWith('en') ? 'en-CA' : 'fr-CA');

/** Montant CAD entier (« 2 345 782 $ » / « $2,345,782 »). */
export function formatCad(value: number, locale: string): string {
  return new Intl.NumberFormat(localeTag(locale), {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(value);
}

/** Montant CAD compact pour axes de graphique — locale-aware via Intl. */
export function formatCompactCad(value: number, locale: string): string {
  if (Math.abs(value) < 1_000) return formatCad(value, locale);
  return new Intl.NumberFormat(localeTag(locale), {
    notation: 'compact',
    compactDisplay: 'short',
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(value);
}

/** Montant CAD TOUJOURS signe (« +12 500 $ », « -3 200 $ ») — ecarts uniquement. */
export function formatSignedCad(value: number, locale: string): string {
  return new Intl.NumberFormat(localeTag(locale), {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
    signDisplay: 'exceptZero',
  }).format(value);
}

/** Taux horaire CAD au cent pres (« 34,50 $ » / « $34.50 ») — remplace le HOURLY
 * fr-CA fige du PoC (grille + tableau reel). formatCad arrondit a l'entier. */
export function formatHourly(value: number, locale: string): string {
  return new Intl.NumberFormat(localeTag(locale), {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Ecart de taux horaire au cent pres, signe TOUJOURS affiche (« +1,25 $ »). */
export function formatSignedHourly(value: number, locale: string): string {
  return new Intl.NumberFormat(localeTag(locale), {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    signDisplay: 'exceptZero',
  }).format(value);
}

/** Pourcentage SANS signe (« 92 % ») — parts/atteintes (boni). '' n'est jamais
 * passe ici (appelants garantissent un nombre fini). */
export function formatPctPlain(ratio: number, locale: string): string {
  return new Intl.NumberFormat(localeTag(locale), {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(ratio);
}

/** Ratio (0.032) → « +3,2 % » (signe force) — ecart % de masse. « — » si non fini. */
export function formatSignedPct(ratio: number | null, locale: string): string {
  if (ratio === null || !Number.isFinite(ratio)) return '—';
  return new Intl.NumberFormat(localeTag(locale), {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    signDisplay: 'exceptZero',
  }).format(ratio);
}

/** ISO → date courte locale (« 2026-04-30 » / « 4/30/2026 »). '' si vide/invalide. */
export function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(localeTag(locale), { dateStyle: 'medium' }).format(d);
}

/** ISO → heure courte locale (« 14:05 » / « 2:05 PM »). '' si vide/invalide. */
export function formatTime(iso: string | null, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(localeTag(locale), { timeStyle: 'short' }).format(d);
}

/** Nombre simple, au plus deux decimales (« 1,5 » / « 1.5 ») — seuils et facteurs. */
export function formatDecimal(value: number, locale: string): string {
  return new Intl.NumberFormat(localeTag(locale), { maximumFractionDigits: 2 }).format(value);
}

/** Annee abregee (2025 → « ’25 ») — marqueur de periode passee, identique en FR et en EN. */
export function formatShortYear(year: number): string {
  return `’${String(year % 100).padStart(2, '0')}`;
}

/** Fleche « avant → apres » des lignes d'ecart — pictogramme, identique en FR et en EN. */
export const CHANGE_ARROW = '→';
